import {
  Button,
  CircularProgress,
  FormControlLabel,
  Switch,
  TextField
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { FiImage, FiPlus, FiSave, FiTrash2, FiUpload } from "react-icons/fi";
import { makeToasterErrorHandler } from "../../common/http/makeToasterErrorHandler";
import {
  ReportBrandingContactInterface,
  ReportBrandingImageSlot,
  ReportBrandingInterface
} from "../../common/interfaces";
import CustomTooltip from "../../components/CustomTooltip";
import PageToaster from "../../components/toaster/PageToaster";
import { useAuth } from "../../hooks/useAuth";
import { usePageToast } from "../../hooks/usePageToast";
import {
  fetchReportBranding,
  removeReportBrandingImage,
  saveReportBranding,
  uploadReportBrandingImage
} from "./actions";
import {
  Actions,
  Card,
  CardTitle,
  ColorRow,
  ContactBlock,
  Container,
  FieldsGrid,
  ImagePreview,
  ImageSlotCard,
  ImageSlots,
  SlotActions,
  Subtitle,
  SwitchGrid,
  Title
} from "./report-branding.styled";

const DEFAULT_ACCENT = "#A81E5E";
const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
const ACCEPTED_TYPES = ["image/png", "image/jpeg", "image/jpg"];

type FormState = {
  cover_title: string;
  cover_tagline_1: string;
  cover_tagline_2: string;
  accent_color: string;
  about_title: string;
  about_text: string;
  support_title: string;
  support_text: string;
  site_url: string;
  contacts: ReportBrandingContactInterface[];
  show_cover: boolean;
  show_about_page: boolean;
  show_support_page: boolean;
  show_index: boolean;
  show_contacts_page: boolean;
};

const EMPTY_FORM: FormState = {
  cover_title: "Relatório Técnico",
  cover_tagline_1: "",
  cover_tagline_2: "",
  accent_color: DEFAULT_ACCENT,
  about_title: "",
  about_text: "",
  support_title: "",
  support_text: "",
  site_url: "",
  contacts: [],
  show_cover: true,
  show_about_page: false,
  show_support_page: false,
  show_index: true,
  show_contacts_page: true
};

const IMAGE_SLOTS: {
  slot: ReportBrandingImageSlot;
  name: string;
  hint: string;
  previewKey: keyof ReportBrandingInterface;
}[] = [
  {
    slot: "cover_logo",
    name: "Logo da capa",
    hint: "Aparece grande no topo da capa. Vazio: usa a logo cadastrada na empresa.",
    previewKey: "cover_logo_url_signed"
  },
  {
    slot: "cover_image",
    name: "Arte da capa",
    hint: "Imagem decorativa no meio da capa. Opcional.",
    previewKey: "cover_image_url_signed"
  },
  {
    slot: "support_image",
    name: "Imagem da assistência",
    hint: "Ilustra a página institucional de assistência técnica. Opcional.",
    previewKey: "support_image_url_signed"
  }
];

/** Normaliza um hex digitado à mão para o formato que o backend aceita. */
function normalizeHex(value: string): string {
  const raw = value.trim();
  if (!raw) return DEFAULT_ACCENT;
  const withHash = raw.startsWith("#") ? raw : `#${raw}`;
  return /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(withHash)
    ? withHash.toUpperCase()
    : DEFAULT_ACCENT;
}

export default function ReportBrandingPage() {
  const [branding, setBranding] = useState<ReportBrandingInterface | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingSlot, setUploadingSlot] =
    useState<ReportBrandingImageSlot | null>(null);

  const fileInputs = {
    cover_logo: useRef<HTMLInputElement>(null),
    cover_image: useRef<HTMLInputElement>(null),
    support_image: useRef<HTMLInputElement>(null)
  };

  const { isUser } = useAuth();
  const readOnly = isUser;

  const containerId = "toaster-report-branding-page";
  const { autoSuccess, autoError } = usePageToast(containerId, true);
  const handleHttpError = makeToasterErrorHandler(autoError);

  const applyBranding = (data: ReportBrandingInterface | null) => {
    setBranding(data);
    if (!data) {
      setForm(EMPTY_FORM);
      return;
    }
    setForm({
      cover_title: data.cover_title ?? EMPTY_FORM.cover_title,
      cover_tagline_1: data.cover_tagline_1 ?? "",
      cover_tagline_2: data.cover_tagline_2 ?? "",
      accent_color: data.accent_color ?? DEFAULT_ACCENT,
      about_title: data.about_title ?? "",
      about_text: data.about_text ?? "",
      support_title: data.support_title ?? "",
      support_text: data.support_text ?? "",
      site_url: data.site_url ?? "",
      contacts: data.contacts ?? [],
      show_cover: data.show_cover,
      show_about_page: data.show_about_page,
      show_support_page: data.show_support_page,
      show_index: data.show_index,
      show_contacts_page: data.show_contacts_page
    });
  };

  const load = async () => {
    setLoading(true);
    try {
      applyBranding(await fetchReportBranding());
    } catch (error) {
      console.error("Erro ao buscar identidade do relatório:", error);
      handleHttpError(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setField = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const saved = await saveReportBranding({
        ...form,
        accent_color: normalizeHex(form.accent_color),
        // Blocos sem título não têm o que imprimir no PDF.
        contacts: form.contacts
          .map((contact) => ({
            title: contact.title.trim(),
            lines: contact.lines.filter((line) => line.trim() !== "")
          }))
          .filter((contact) => contact.title !== "")
      });
      applyBranding(saved);
      autoSuccess("Identidade do relatório salva com sucesso!");
    } catch (error) {
      console.error("Erro ao salvar identidade do relatório:", error);
      handleHttpError(error);
    } finally {
      setSaving(false);
    }
  };

  const handlePickFile = (slot: ReportBrandingImageSlot) =>
    fileInputs[slot].current?.click();

  const handleUpload = async (
    slot: ReportBrandingImageSlot,
    file: File | undefined
  ) => {
    if (!file) return;
    if (!ACCEPTED_TYPES.includes(file.type)) {
      autoError("A imagem deve ser PNG ou JPG.");
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      autoError("A imagem excede 5 MB.");
      return;
    }

    setUploadingSlot(slot);
    try {
      applyBranding(await uploadReportBrandingImage(slot, file));
      autoSuccess("Imagem enviada!");
    } catch (error) {
      console.error("Erro ao enviar imagem:", error);
      handleHttpError(error);
    } finally {
      setUploadingSlot(null);
      if (fileInputs[slot].current) fileInputs[slot].current!.value = "";
    }
  };

  const handleRemoveImage = async (slot: ReportBrandingImageSlot) => {
    setUploadingSlot(slot);
    try {
      applyBranding(await removeReportBrandingImage(slot));
      autoSuccess("Imagem removida do relatório.");
    } catch (error) {
      console.error("Erro ao remover imagem:", error);
      handleHttpError(error);
    } finally {
      setUploadingSlot(null);
    }
  };

  const addContact = () =>
    setField("contacts", [...form.contacts, { title: "", lines: [] }]);

  const updateContact = (
    index: number,
    patch: Partial<ReportBrandingContactInterface>
  ) =>
    setField(
      "contacts",
      form.contacts.map((contact, i) =>
        i === index ? { ...contact, ...patch } : contact
      )
    );

  const removeContact = (index: number) =>
    setField(
      "contacts",
      form.contacts.filter((_, i) => i !== index)
    );

  if (loading) {
    return (
      <Container>
        <PageToaster containerId={containerId} />
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container>
      <PageToaster containerId={containerId} />

      <Title>
        <FiImage /> Identidade do Relatório Técnico
      </Title>
      <Subtitle>
        Logo, textos e cores que a sua empresa imprime no PDF do relatório.
        Campos em branco simplesmente não aparecem no documento.
      </Subtitle>

      <Card>
        <CardTitle>Imagens</CardTitle>
        <ImageSlots>
          {IMAGE_SLOTS.map(({ slot, name, hint, previewKey }) => {
            const preview = branding?.[previewKey] as string | null | undefined;
            const busy = uploadingSlot === slot;
            return (
              <ImageSlotCard key={slot}>
                <span className="slot-name">{name}</span>
                <span className="slot-hint">{hint}</span>
                <ImagePreview>
                  {preview ? (
                    <img src={preview} alt={name} />
                  ) : (
                    <span>Sem imagem</span>
                  )}
                </ImagePreview>
                <input
                  ref={fileInputs[slot]}
                  type="file"
                  accept="image/png,image/jpeg"
                  style={{ display: "none" }}
                  onChange={(e) => handleUpload(slot, e.target.files?.[0])}
                />
                <SlotActions>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={busy ? undefined : <FiUpload />}
                    disabled={busy || readOnly}
                    onClick={() => handlePickFile(slot)}
                  >
                    {busy ? <CircularProgress size={18} /> : "Enviar"}
                  </Button>
                  {preview && (
                    <CustomTooltip title="Remover imagem do relatório">
                      <span>
                        <Button
                          size="small"
                          color="error"
                          disabled={busy || readOnly}
                          onClick={() => handleRemoveImage(slot)}
                        >
                          <FiTrash2 />
                        </Button>
                      </span>
                    </CustomTooltip>
                  )}
                </SlotActions>
              </ImageSlotCard>
            );
          })}
        </ImageSlots>
      </Card>

      <Card>
        <CardTitle>Capa</CardTitle>
        <FieldsGrid>
          <TextField
            label="Título da capa"
            value={form.cover_title}
            disabled={readOnly}
            onChange={(e) => setField("cover_title", e.target.value)}
          />
          <TextField
            label="Slogan (linha 1)"
            placeholder="Alta tecnologia"
            value={form.cover_tagline_1}
            disabled={readOnly}
            onChange={(e) => setField("cover_tagline_1", e.target.value)}
          />
          <TextField
            label="Slogan (linha 2)"
            placeholder="Alta confiabilidade"
            value={form.cover_tagline_2}
            disabled={readOnly}
            onChange={(e) => setField("cover_tagline_2", e.target.value)}
          />
        </FieldsGrid>
        <ColorRow>
          <input
            type="color"
            value={normalizeHex(form.accent_color)}
            disabled={readOnly}
            onChange={(e) => setField("accent_color", e.target.value)}
          />
          <TextField
            label="Cor de destaque"
            helperText="Usada nas réguas, títulos e na página de contatos."
            value={form.accent_color}
            disabled={readOnly}
            onChange={(e) => setField("accent_color", e.target.value)}
          />
        </ColorRow>
      </Card>

      <Card>
        <CardTitle>Páginas institucionais</CardTitle>
        <FieldsGrid>
          <TextField
            label="Título da página institucional"
            placeholder="Nome da empresa"
            value={form.about_title}
            disabled={readOnly}
            onChange={(e) => setField("about_title", e.target.value)}
          />
          <TextField
            label="Título da página de assistência"
            placeholder="Assistências Técnicas"
            value={form.support_title}
            disabled={readOnly}
            onChange={(e) => setField("support_title", e.target.value)}
          />
        </FieldsGrid>
        <TextField
          label="Texto institucional"
          helperText="Cada linha vira um parágrafo no PDF."
          multiline
          minRows={4}
          value={form.about_text}
          disabled={readOnly}
          onChange={(e) => setField("about_text", e.target.value)}
        />
        <TextField
          label="Texto da assistência técnica"
          helperText="Cada linha vira um parágrafo no PDF."
          multiline
          minRows={4}
          value={form.support_text}
          disabled={readOnly}
          onChange={(e) => setField("support_text", e.target.value)}
        />
      </Card>

      <Card>
        <CardTitle>Contatos</CardTitle>
        <TextField
          label="Site"
          placeholder="www.suaempresa.com.br"
          value={form.site_url}
          disabled={readOnly}
          onChange={(e) => setField("site_url", e.target.value)}
        />

        {form.contacts.map((contact, index) => (
          <ContactBlock key={index}>
            <TextField
              label="Título do bloco"
              placeholder="Dep. Comercial"
              value={contact.title}
              disabled={readOnly}
              onChange={(e) => updateContact(index, { title: e.target.value })}
            />
            <TextField
              label="Linhas"
              helperText="Uma linha por telefone, e-mail ou informação."
              multiline
              minRows={2}
              value={contact.lines.join("\n")}
              disabled={readOnly}
              onChange={(e) =>
                updateContact(index, { lines: e.target.value.split("\n") })
              }
            />
            <CustomTooltip title="Remover bloco">
              <span>
                <Button
                  color="error"
                  disabled={readOnly}
                  onClick={() => removeContact(index)}
                >
                  <FiTrash2 />
                </Button>
              </span>
            </CustomTooltip>
          </ContactBlock>
        ))}

        <div>
          <Button
            startIcon={<FiPlus />}
            disabled={readOnly}
            onClick={addContact}
          >
            Adicionar bloco de contato
          </Button>
        </div>
      </Card>

      <Card>
        <CardTitle>Folhas do relatório</CardTitle>
        <SwitchGrid>
          <FormControlLabel
            control={
              <Switch
                checked={form.show_cover}
                disabled={readOnly}
                onChange={(e) => setField("show_cover", e.target.checked)}
              />
            }
            label="Capa"
          />
          <FormControlLabel
            control={
              <Switch
                checked={form.show_about_page}
                disabled={readOnly || !form.about_text.trim()}
                onChange={(e) => setField("show_about_page", e.target.checked)}
              />
            }
            label="Página institucional"
          />
          <FormControlLabel
            control={
              <Switch
                checked={form.show_support_page}
                disabled={readOnly || !form.support_text.trim()}
                onChange={(e) =>
                  setField("show_support_page", e.target.checked)
                }
              />
            }
            label="Página de assistência"
          />
          <FormControlLabel
            control={
              <Switch
                checked={form.show_index}
                disabled={readOnly}
                onChange={(e) => setField("show_index", e.target.checked)}
              />
            }
            label="Índice"
          />
          <FormControlLabel
            control={
              <Switch
                checked={form.show_contacts_page}
                disabled={readOnly}
                onChange={(e) =>
                  setField("show_contacts_page", e.target.checked)
                }
              />
            }
            label="Página de contatos"
          />
        </SwitchGrid>
        <Subtitle>
          As páginas institucionais só ficam disponíveis depois que o texto
          correspondente for preenchido.
        </Subtitle>
      </Card>

      {!readOnly && (
        <Actions>
          <Button onClick={load} disabled={saving}>
            Descartar alterações
          </Button>
          <Button
            variant="contained"
            startIcon={saving ? undefined : <FiSave />}
            disabled={saving}
            onClick={handleSave}
          >
            {saving ? <CircularProgress size={20} /> : "Salvar"}
          </Button>
        </Actions>
      )}
    </Container>
  );
}
